import * as yup from 'yup'

export const campaignConfigSchema = yup
	.object({
		name: yup
			.string()
			.required('Campaign name is required')
			.min(6, 'Campaign name must be at least 6 characters')
			.max(100, 'Campaign name must be less than 100 characters'),

		rruleType: yup
			.mixed<'recurrent' | 'one_per_day' | 'one_time_only'>()
			.oneOf(['recurrent', 'one_per_day', 'one_time_only'])
			.required(),

		startDate: yup
			.date()
			.nullable()
			.notRequired()
			.test(
				'is-future-or-today',
				'Start date cannot be in the past',
				function (value) {
					// When editing an existing campaign we allow keeping (or even
					// changing) the start date to a past day. The restriction
					// should only apply when creating a brand new campaign.
					const ctx = (this?.options?.context || {}) as { isEdit?: boolean }
					if (ctx.isEdit) {
						return true
					}

					if (!value) return true
					const today = new Date()
					today.setHours(0, 0, 0, 0)
					return value >= today
				}
			),

		endDate: yup.date().nullable().notRequired(),

		playEvery: yup
			.number()
			.typeError('Must be at least 1')
			.min(1, 'Must be at least 1')
			.max(1440, 'Cannot exceed 1440 (24 hours)'),

		playEveryUnit: yup
			.mixed<'minutes' | 'hours'>()
			.oneOf(['minutes', 'hours'])
			.required(),

		selectedDays: yup
			.array(
				yup
					.mixed<'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU'>()
					.oneOf(['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'])
			)
			// Empty array means "all days" in the UI semantics, so no
			// additional validation is required here.
			.default([]),

		timeFrom: yup
			.string()
			.matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),

		timeTo: yup
			.string()
			.matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),

		exdate: yup.array(yup.string()).default([]),

		priority: yup.boolean().default(false),

		interruptsMusic: yup.boolean().default(false),

		type: yup
			.mixed<'opening' | 'closing' | 'other'>()
			.oneOf(['opening', 'closing', 'other'])
			.default('other'),
	})
	.test('end-after-start', 'End date must be after start date', (values) => {
		if (!values) return true
		const { startDate, endDate } = values as {
			startDate?: Date | null
			endDate?: Date | null
		}
		if (startDate && endDate) {
			return endDate >= startDate
		}
		return true
	})
	.required()

export type CampaignConfigFormValues = yup.InferType<
	typeof campaignConfigSchema
>

export const uploadAdSchema = yup
	.object({
		advertName: yup.string().when('audioFile', {
			is: (file: File | undefined | null) => !!file,
			then: (schema) =>
				schema
					.required('Advert name must be at least 6 characters')
					.min(6, 'Advert name must be at least 6 characters')
					.max(255, 'Advert name must be less than 255 characters'),
			otherwise: (schema) => schema.notRequired(),
		}),

		description: yup
			.string()
			.max(255, 'Description must be less than 255 characters')
			.notRequired(),

		adStartDate: yup.date().notRequired(),

		adEndDate: yup.date().notRequired(),

		audioFile: yup
			.mixed<File>()
			.test(
				'file-type',
				'Only .mp3 files are allowed',
				(file) => !file || file.type === 'audio/mpeg'
			)
			.test(
				'file-size',
				'File size must be less than 10MB',
				(file) => !file || file.size <= 10 * 1024 * 1024
			),

		/** Pending adverts to upload on submit. */
		pendingAdverts: yup
			.array(
				yup.object({
					audioFile: yup
						.mixed<File>()
						.required()
						.test(
							'file-type',
							'Only .mp3 files are allowed',
							(file) => !file || file.type === 'audio/mpeg'
						)
						.test(
							'file-size',
							'File size must be less than 10MB',
							(file) => !file || file.size <= 10 * 1024 * 1024
						),
					advertName: yup
						.string()
						.required('Advert name must be at least 6 characters')
						.min(6, 'Advert name must be at least 6 characters')
						.max(255, 'Advert name must be less than 255 characters'),
					description: yup
						.string()
						.max(255, 'Description must be less than 255 characters')
						.notRequired(),
					adStartDate: yup.date().notRequired(),
					adEndDate: yup.date().notRequired(),
				})
			)
			.default([]),
	})
	.test('end-after-start', 'Ad end date must be after start date', (values) => {
		if (!values?.adStartDate || !values?.adEndDate) {
			return true
		}
		return values.adEndDate >= values.adStartDate
	})

export type PendingAdvertItem = {
	audioFile: File
	advertName: string
	description?: string
	adStartDate?: Date
	adEndDate?: Date
}

export type UploadAdFormValues = yup.InferType<typeof uploadAdSchema>

export const selectAdSchema = yup.object({
	selectedAdId: yup
		.number()
		.typeError('Please select an ad')
		.min(1, 'Please select an ad')
		.required('Please select an ad'),
})

export type SelectAdFormValues = yup.InferType<typeof selectAdSchema>

export type EditAdvertFormValues = {
	advertName: string
	description?: string | null
	adStartDate?: Date | null
	adEndDate?: Date | null
}

export const createEditAdvertSchema = (t: (key: string) => string) =>
	yup.object({
		advertName: yup
			.string()
			.required(t('modals.editAd.validation.nameMin'))
			.min(6, t('modals.editAd.validation.nameMin'))
			.max(255, t('modals.editAd.validation.nameMax')),
		description: yup
			.string()
			.max(255, t('modals.editAd.validation.descriptionMax'))
			.notRequired(),
		adStartDate: yup.date().nullable().notRequired(),
		adEndDate: yup
			.date()
			.nullable()
			.notRequired()
			.test(
				'end-after-start',
				t('modals.editAd.validation.endBeforeStart'),
				function (value) {
					const { adStartDate } = this.parent
					if (!adStartDate || !value) return true
					return value >= adStartDate
				}
			),
	})
